import { useCallback, useMemo, useState } from "react";
import { useUserSession, useFinancialPreferences, useFinancialVisibility } from "shared/contexts/UserContext";
import { useCurrencyConversion } from "shared/contexts/CurrencyConversionContext";
import { useOfflineDisabled } from "shared/hooks/useOfflineDisabled";
import { useTranslation } from "shared/hooks/useTranslation";
import { useWallet } from "features/wallet/hooks/useWallet";
import { formatCurrencyAmount, languageToLocale } from "shared/money/balances";
import { buildCurrencySections } from "features/wallet/lib/walletDisplay";
import type { LiquidityPool } from "features/wallet/api/Wallet";
import { toast } from "sonner";
import { toastApiError } from "lib/toastApiError";

export function useWalletScreen() {
    const { t, currentLanguage } = useTranslation();
    const { profile } = useUserSession();
    const { userSettings } = useFinancialPreferences();
    const { balancesVisible, setBalancesVisible } = useFinancialVisibility();
    const { convertBalances } = useCurrencyConversion();
    const username = profile?.username ?? null;
    const token = profile?.token;
    const locale = languageToLocale(currentLanguage);

    const {
        pools,
        loading,
        error,
        refetch,
        createPool,
        updatePool,
        deletePool,
        transfer,
        exchange,
    } = useWallet(username);

    const { isOnline, offlineTitle, disabledIfOffline } = useOfflineDisabled();
    const mutationsDisabled = disabledIfOffline();

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [poolMenuAnchor, setPoolMenuAnchor] = useState<null | HTMLElement>(null);
    const [poolForEdit, setPoolForEdit] = useState<LiquidityPool | null>(null);
    const [poolForDelete, setPoolForDelete] = useState<LiquidityPool | null>(null);
    const [historyPool, setHistoryPool] = useState<LiquidityPool | null>(null);
    const [addPoolOpen, setAddPoolOpen] = useState(false);
    const [transferOpen, setTransferOpen] = useState(false);
    const [exchangeOpen, setExchangeOpen] = useState(false);

    const totalBalanceByCurrency = useMemo(() => {
        const byCur: Record<string, number> = {};
        pools.forEach((p) => {
            byCur[p.currency] = (byCur[p.currency] ?? 0) + p.currentBalance;
        });
        return byCur;
    }, [pools]);

    const sections = useMemo(() => buildCurrencySections(pools), [pools]);

    const estimatedTotal = useMemo(
        () => convertBalances(totalBalanceByCurrency),
        [convertBalances, totalBalanceByCurrency],
    );

    const pageHeaderBalanceLabel = useMemo(
        () => formatCurrencyAmount(
            Math.abs(estimatedTotal),
            userSettings.displayCurrency,
            locale,
        ),
        [estimatedTotal, locale, userSettings.displayCurrency],
    );

    const subtitleForPool = useCallback(
        (pool: LiquidityPool) => {
            const currency = (pool.currency || "").toUpperCase();
            const name = pool.name.toLowerCase();
            if (currency === "USDT" || currency === "USDC" || name.includes("crypto") || name.includes("nexo")) {
                return t("wallet.cryptoAccount");
            }
            if (currency === "USD") return t("wallet.usdAccount");
            if (name.includes("plazo") || name.includes("ahorro")) return t("wallet.savingsAccount");
            if (name.includes("galicia") || name.includes("ciudad") || name.includes("banco")) return t("wallet.bankAccount");
            return t("wallet.digitalAccount");
        },
        [t],
    );

    const handlePoolMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, pool: LiquidityPool) => {
        event.stopPropagation();
        setPoolMenuAnchor(event.currentTarget);
        setPoolForEdit(pool);
    }, []);

    const handlePoolMenuClose = useCallback(() => {
        setPoolMenuAnchor(null);
    }, []);

    const handleEditClick = useCallback(() => {
        if (poolForEdit) setEditModalOpen(true);
        handlePoolMenuClose();
    }, [handlePoolMenuClose, poolForEdit]);

    const handleDeleteClick = useCallback(async () => {
        const pool = poolForEdit;
        if (!pool) return;
        handlePoolMenuClose();
        setPoolForDelete(pool);
    }, [handlePoolMenuClose, poolForEdit]);

    const handleConfirmDeletePool = useCallback(async () => {
        if (!poolForDelete) return;
        const poolId = poolForDelete._id;
        const result = await deletePool(poolId);
        if (result.success) {
            toast.success(t("wallet.poolDeleted"));
            setPoolForDelete(null);
        } else {
            toastApiError(result.error ?? "", t, "wallet.deletePoolError");
        }
    }, [deletePool, poolForDelete, t]);

    const handlePoolClick = useCallback((pool: LiquidityPool) => {
        setHistoryPool(pool);
    }, []);

    const handleAddPoolShortcut = useCallback(() => {
        setAddPoolOpen(true);
    }, []);

    const initialLoading = loading && pools.length === 0;

    return {
        t,
        locale,
        username,
        token,
        userSettings,
        balancesVisible,
        setBalancesVisible,
        pools,
        loading,
        error,
        refetch,
        createPool,
        updatePool,
        deletePool,
        poolDelete: { pool: poolForDelete, cancel: () => setPoolForDelete(null), confirm: handleConfirmDeletePool },
        transfer,
        exchange,
        offline: { isOnline, offlineTitle, disabledIfOffline, mutationsDisabled },
        totals: {
            totalBalanceByCurrency,
            sections,
            estimatedTotal,
        },
        pageHeader: {
            balanceLabel: pageHeaderBalanceLabel,
            balanceColor: (estimatedTotal >= 0 ? "success" : "error") as "success" | "error",
        },
        dialogs: {
            editModalOpen,
            setEditModalOpen,
            poolForEdit,
            setPoolForEdit,
            historyPool,
            setHistoryPool,
            addPoolOpen,
            setAddPoolOpen,
            transferOpen,
            setTransferOpen,
            exchangeOpen,
            setExchangeOpen,
        },
        poolMenu: {
            poolMenuAnchor,
            handlePoolMenuOpen,
            handlePoolMenuClose,
            handleEditClick,
            handleDeleteClick,
        },
        actions: {
            handlePoolClick,
            handleAddPoolShortcut,
            subtitleForPool,
        },
        initialLoading,
    };
}
